import React, { useEffect, useState, useRef } from "react";
import { useUnsavedChanges } from "hooks/useUnsavedChanges";
import PropTypes from "prop-types";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { getClientSecrets, saveClientSecrets } from "services/tenantUserAdminService";

const FS = { sm: "0.72rem", md: "0.8rem", base: "0.88rem" };
const TC = { primary: "#0a1628", secondary: "#4a6080", error: "#d32f2f", success: "#2e7d32" };

export default function SecretsTab({ tenantId, clientId }) {
  const [csAuthCode, setCsAuthCode]   = useState("");
  const [chUsername, setChUsername]   = useState("");
  const [chPassword, setChPassword]   = useState("");
  const [ggUsername, setGgUsername]   = useState("");
  const [ggPassword, setGgPassword]   = useState("");
  const [directorVerificationCode, setDirectorVerificationCode] = useState("");
  const [directorVerificationCodeChanged, setDirectorVerificationCodeChanged] = useState(false);
  const [hasDirectorVerificationCode, setHasDirectorVerificationCode] = useState(false);
  const [directorVerificationCodeMask, setDirectorVerificationCodeMask] = useState("");
  const [showCode, setShowCode]       = useState(false);
  const [showPass, setShowPass]       = useState(false);
  const [showGgPass, setShowGgPass]   = useState(false);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState("");

  const initialSnap = useRef(null);
  const isDirty = initialSnap.current !== null && JSON.stringify({ csAuthCode, chUsername, chPassword, ggUsername, ggPassword }) !== initialSnap.current;
  const { guardClose, ConfirmDialog } = useUnsavedChanges(isDirty);

  useEffect(() => {
    if (!tenantId || !clientId) return;
    setLoading(true);
    getClientSecrets(tenantId, clientId)
      .then((s) => {
        const fresh = {
          csAuthCode: s?.csAuthCode || "",
          chUsername: s?.chUsername || "",
          chPassword: s?.chPassword || "",
          ggUsername: s?.ggUsername || "",
          ggPassword: s?.ggPassword || "",
        };
        setCsAuthCode(fresh.csAuthCode);
        setChUsername(fresh.chUsername);
        setChPassword(fresh.chPassword);
        setGgUsername(fresh.ggUsername);
        setGgPassword(fresh.ggPassword);
        // The verification code is deliberately write-only: the API returns
        // only its presence, never its value.
        setDirectorVerificationCode("");
        setDirectorVerificationCodeChanged(false);
        setHasDirectorVerificationCode(Boolean(s?.hasDirectorVerificationCode));
        setDirectorVerificationCodeMask(s?.directorVerificationCodeMask || "");
        initialSnap.current = JSON.stringify(fresh);
      })
      .catch(() => setError("Failed to load secrets."))
      .finally(() => setLoading(false));
  }, [tenantId, clientId]);

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const fields = { csAuthCode, chUsername, chPassword, ggUsername, ggPassword };
      if (directorVerificationCodeChanged) fields.directorVerificationCode = directorVerificationCode;
      const result = await saveClientSecrets(tenantId, clientId, fields);
      if (directorVerificationCodeChanged) {
        setDirectorVerificationCode("");
        setDirectorVerificationCodeChanged(false);
        setHasDirectorVerificationCode(Boolean(result?.hasDirectorVerificationCode));
        setDirectorVerificationCodeMask(result?.directorVerificationCodeMask || "");
      }
      setSaved(true);
      initialSnap.current = JSON.stringify({ csAuthCode, chUsername, chPassword, ggUsername, ggPassword });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <VuiBox>
      {/* Header */}
      <VuiBox display="flex" alignItems="center" gap={1} mb={2.5}>
        <LockIcon sx={{ color: TC.secondary, fontSize: "1.1rem" }} />
        <VuiTypography variant="button" sx={{ fontWeight: 700, color: TC.primary, fontSize: FS.base }}>
          Client Secrets
        </VuiTypography>
        <VuiBox ml="auto" px={1.2} py={0.4}
          sx={{ borderRadius: "6px", background: "rgba(211,47,47,0.08)", border: "1px solid rgba(211,47,47,0.25)" }}>
          <VuiTypography sx={{ fontSize: "0.66rem", color: TC.error, fontWeight: 700, letterSpacing: "0.06em" }}>
            ADMIN ONLY
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} sx={{ color: TC.link }} />
        </VuiBox>
      ) : (
        <VuiBox maxWidth={480}>
          {/* CS01 Auth Code */}
          <VuiBox mb={2.5}>
            <VuiTypography sx={{ fontSize: FS.sm, fontWeight: 600, color: TC.secondary, mb: 0.6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Companies House Authorisation Code
            </VuiTypography>
            <VuiTypography sx={{ fontSize: FS.sm, color: TC.secondary, mb: 1.2, lineHeight: 1.5 }}>
              The 6-character authentication code posted by Companies House to the registered office.
              Used when filing CS01 (and other forms) via the CH API.
            </VuiTypography>
            <TextField
              value={csAuthCode}
              onChange={(e) => { setCsAuthCode(e.target.value.toUpperCase()); setSaved(false); }}
              type={showCode ? "text" : "password"}
              placeholder="e.g. ABC123"
              inputProps={{ maxLength: 6, style: { letterSpacing: showCode ? "0.25em" : "0.1em", fontFamily: "monospace", fontSize: "1rem" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showCode ? "Hide" : "Show"}>
                      <IconButton
                        size="small"
                        onClick={() => setShowCode(v => !v)}
                        edge="end"
                        aria-label={showCode ? "Hide Companies House authorisation code" : "Show Companies House authorisation code"}
                      >
                        {showCode ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              fullWidth size="small"
              sx={{ background: "#f8fbff", borderRadius: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "1rem" } }}
            />
          </VuiBox>

          {/* Director verification code — intentionally write-only */}
          <VuiBox mb={2.5}>
            <VuiTypography sx={{ fontSize: FS.sm, fontWeight: 600, color: TC.secondary, mb: 0.6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Director Verification Code
            </VuiTypography>
            <VuiTypography sx={{ fontSize: FS.sm, color: TC.secondary, mb: 1.2, lineHeight: 1.5 }}>
              {hasDirectorVerificationCode
                ? `A code is securely stored as ${directorVerificationCodeMask || "masked"}. Enter a replacement to update it, or clear this field after editing to remove it.`
                : "Store the code securely for CS01 filing checks when director verification applies. It is never displayed or sent to Companies House."}
            </VuiTypography>
            <TextField
              value={directorVerificationCode}
              onChange={(e) => {
                setDirectorVerificationCode(e.target.value);
                setDirectorVerificationCodeChanged(true);
                setSaved(false);
              }}
              type="password"
              placeholder={hasDirectorVerificationCode
                ? directorVerificationCodeMask || "Enter replacement code"
                : "Enter director verification code"}
              inputProps={{ maxLength: 64, autoComplete: "new-password" }}
              error={directorVerificationCodeChanged && Boolean(error)}
              helperText={
                directorVerificationCodeChanged && error
                  ? error
                  : hasDirectorVerificationCode
                    ? "Saved code is masked and cannot be retrieved."
                    : "Use 6–64 letters, numbers, or hyphens."
              }
              fullWidth size="small"
              sx={{ background: "#f8fbff", borderRadius: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.9rem" } }}
            />
          </VuiBox>

          {/* Companies House Login */}
          <VuiBox mb={2.5}>
            <VuiTypography sx={{ fontSize: FS.sm, fontWeight: 600, color: TC.secondary, mb: 0.6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Companies House WebFiling Credentials
            </VuiTypography>
            <VuiTypography sx={{ fontSize: FS.sm, color: TC.secondary, mb: 1.2, lineHeight: 1.5 }}>
              Username and password for the Companies House WebFiling account.
            </VuiTypography>
            <TextField
              value={chUsername}
              onChange={(e) => { setChUsername(e.target.value); setSaved(false); }}
              placeholder="WebFiling username"
              fullWidth size="small"
              sx={{ mb: 1.2, background: "#f8fbff", borderRadius: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.9rem" } }}
            />
            <TextField
              value={chPassword}
              onChange={(e) => { setChPassword(e.target.value); setSaved(false); }}
              type={showPass ? "text" : "password"}
              placeholder="WebFiling password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPass ? "Hide" : "Show"}>
                      <IconButton
                        size="small"
                        onClick={() => setShowPass(v => !v)}
                        edge="end"
                        aria-label={showPass ? "Hide Companies House WebFiling password" : "Show Companies House WebFiling password"}
                      >
                        {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              fullWidth size="small"
              sx={{ background: "#f8fbff", borderRadius: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.9rem" } }}
            />
          </VuiBox>

          {/* Government Gateway Credentials */}
          <VuiBox mb={2.5}>
            <VuiTypography sx={{ fontSize: FS.sm, fontWeight: 600, color: TC.secondary, mb: 0.6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Government Gateway Credentials
            </VuiTypography>
            <VuiTypography sx={{ fontSize: FS.sm, color: TC.secondary, mb: 1.2, lineHeight: 1.5 }}>
              User ID and password for the client's HMRC Government Gateway account.
            </VuiTypography>
            <TextField
              value={ggUsername}
              onChange={(e) => { setGgUsername(e.target.value); setSaved(false); }}
              placeholder="Government Gateway user ID"
              fullWidth size="small"
              sx={{ mb: 1.2, background: "#f8fbff", borderRadius: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.9rem" } }}
            />
            <TextField
              value={ggPassword}
              onChange={(e) => { setGgPassword(e.target.value); setSaved(false); }}
              type={showGgPass ? "text" : "password"}
              placeholder="Government Gateway password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showGgPass ? "Hide" : "Show"}>
                      <IconButton
                        size="small"
                        onClick={() => setShowGgPass(v => !v)}
                        edge="end"
                        aria-label={showGgPass ? "Hide Government Gateway password" : "Show Government Gateway password"}
                      >
                        {showGgPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              fullWidth size="small"
              sx={{ background: "#f8fbff", borderRadius: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.9rem" } }}
            />
          </VuiBox>

          {/* Error */}
          {error && (
            <VuiBox mb={1.5} px={1.5} py={1} sx={{ borderRadius: "6px", background: "rgba(211,47,47,0.07)", border: "1px solid rgba(211,47,47,0.3)" }}>
              <VuiTypography sx={{ fontSize: FS.sm, color: TC.error }}>{error}</VuiTypography>
            </VuiBox>
          )}

          {/* Save */}
          <VuiBox display="flex" alignItems="center" gap={1.5} mt={2}>
            <VuiButton
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving
                ? <CircularProgress size={13} sx={{ color: "inherit" }} />
                : saved
                  ? <CheckCircleIcon sx={{ fontSize: "13px !important" }} />
                  : <SaveIcon sx={{ fontSize: "13px !important" }} />}
              sx={{
                fontSize: FS.md, px: 2.5,
                background: saved
                  ? "linear-gradient(135deg,#2e7d32,#43a047)"
                  : "linear-gradient(135deg,#1565c0,#1976d2)",
                "&:hover:not(:disabled)": {
                  background: saved
                    ? "linear-gradient(135deg,#1b5e20,#2e7d32)"
                    : "linear-gradient(135deg,#0d47a1,#1565c0)",
                },
              }}>
              {saving ? "Saving…" : saved ? "Saved ✓" : "Save Secrets"}
            </VuiButton>
          </VuiBox>
        </VuiBox>
      )}
    {ConfirmDialog}
    </VuiBox>
  );
}

SecretsTab.propTypes = {
  tenantId: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
};
